import { useCallback } from 'react';
import { flushSync } from 'react-dom';
import { Chapter, ChapterVersion } from '../../../types';
import { dataCache } from '../../../cache/DataCache';
import { ensureVersionQuota } from '../../../utils/chapterVersions';

interface UseChapterVersionOptions {
  bookId: string;
  selectedChapter: Chapter | null;
  chapterContent: string;
  setChapterContent: (content: string) => void;
  setSelectedChapter: (chapter: Chapter) => void;
  setIsChapterContentLoading: (loading: boolean) => void;
  loadVolumes: () => Promise<void>;
  setVersionRefreshKey: () => void;
  setConfirmModal: (modal: { show: boolean; title: string; message: string; onConfirm: () => void; onCancel?: () => void; confirmText?: string; cancelText?: string } | null) => void;
  setActiveView: (view: string) => void;
}

export function useChapterVersion({
  bookId,
  selectedChapter,
  chapterContent,
  setChapterContent,
  setSelectedChapter,
  setIsChapterContentLoading,
  loadVolumes,
  setVersionRefreshKey,
  setConfirmModal,
  setActiveView,
}: UseChapterVersionOptions) {
  const handleOpenVersionHistory = useCallback(() => {
    if (selectedChapter) {
      setVersionRefreshKey();
      setActiveView('version-history');
    }
  }, [selectedChapter, setVersionRefreshKey, setActiveView]);

  const handleSaveChapter = useCallback(async (title: string, summary: string, content: string) => {
    if (!selectedChapter) return;
    await window.api.updateChapter({ id: selectedChapter.id, title, summary, content });
    const updatedMeta = await window.api.getChapterMetadata(selectedChapter.id);
    if (updatedMeta) {
      setSelectedChapter(updatedMeta);
      setChapterContent(content);
      loadVolumes();
      // 同步刷新缓存（就地更新，保证全局搜索索引、章节树、剧情概览即时最新）
      dataCache.patchChapter(bookId, updatedMeta);
      dataCache.patchChapterContent(bookId, updatedMeta.id, content);
      window.dispatchEvent(new Event('xiaotianshu:data-changed'));
    }
  }, [selectedChapter, setSelectedChapter, setChapterContent, loadVolumes, bookId]);

  const handleRestoreVersion = useCallback(async (version: ChapterVersion) => {
    if (!selectedChapter) return;
    const currentContent = chapterContent;
    const currentTitle = selectedChapter.title;
    const currentSummary = selectedChapter.summary || '';
    try {
      await ensureVersionQuota(selectedChapter.id, 'manual_save');
      await window.api.createChapterVersion({
        chapterId: selectedChapter.id,
        bookId: selectedChapter.book_id,
        title: currentTitle,
        summary: currentSummary,
        content: currentContent,
        wordCount: selectedChapter.word_count,
        source: 'manual_save',
      });
    } catch { /* ignore */ }

    await window.api.updateChapter({
      id: selectedChapter.id,
      title: version.title,
      summary: version.summary,
      content: version.content,
    });
    const updatedMeta = await window.api.getChapterMetadata(selectedChapter.id);
    if (updatedMeta) {
      // 同步刷新缓存（元数据 + 正文），保证章节树/全局搜索/剧情概览即时最新
      dataCache.patchChapter(bookId, updatedMeta);
      dataCache.patchChapterContent(bookId, updatedMeta.id, version.content);
      window.dispatchEvent(new Event('xiaotianshu:data-changed'));
      // 强制编辑器重新挂载并加载恢复后的内容：先进入加载态（卸载旧编辑器），
      // 再以恢复后的章节/内容重新挂载，避免残留旧内容显示
      flushSync(() => setIsChapterContentLoading(true));
      setSelectedChapter(updatedMeta);
      setChapterContent(version.content);
      flushSync(() => setIsChapterContentLoading(false));
      void loadVolumes();
    }
  }, [selectedChapter, chapterContent, setChapterContent, setSelectedChapter, setIsChapterContentLoading, loadVolumes, bookId]);

  const handleDeleteVersion = useCallback(async (versionId: string) => {
    setConfirmModal({
      show: true,
      title: '删除历史版本',
      message: '确定要删除该历史版本吗？此操作不可撤销。',
      confirmText: '删除',
      cancelText: '取消',
      onConfirm: async () => {
        await window.api.deleteChapterVersion(versionId);
        setVersionRefreshKey();
        setConfirmModal(null);
      },
      onCancel: () => setConfirmModal(null),
    });
  }, [setConfirmModal, setVersionRefreshKey]);

  const handleVersionSaved = useCallback(() => {
    setVersionRefreshKey();
  }, [setVersionRefreshKey]);

  return {
    handleOpenVersionHistory,
    handleSaveChapter,
    handleRestoreVersion,
    handleDeleteVersion,
    handleVersionSaved,
  };
}
